import { AlertTriangle, ArrowUpRight, Boxes, ClipboardList, PackageCheck, Wallet, CalendarClock, Truck } from "lucide-react";
import { money } from "../lib/api";
import StatusBadge from "../components/StatusBadge";

function Stat({ label, value, change, icon: Icon, tone, test }) {
  return (
    <div className="stat-card" data-testid={test}>
      <div className={`stat-icon ${tone}`}><Icon size={19} /></div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{change}</small>
    </div>
  );
}

export default function Dashboard({ data, onNavigate }) {
  const s = data.stats || {};
  return (
    <>
      <section className="stat-grid">
        <Stat label="Inventory value" value={money(s.inventory_value)} change="Across active stock" icon={Boxes} tone="teal" test="inventory-value-stat" />
        <Stat label="Active items" value={s.items || 0} change="Medicine + equipment" icon={PackageCheck} tone="blue" test="active-items-stat" />
        <Stat label="Low stock alerts" value={s.low_stock || 0} change="Needs replenishment" icon={AlertTriangle} tone="amber" test="low-stock-stat" />
        <Stat label="Open indents" value={s.pending_indents || 0} change="Awaiting store action" icon={ClipboardList} tone="red" test="open-indents-stat" />
      </section>
      <section className="stat-grid secondary">
        <Stat label="Payables outstanding" value={money(s.payables)} change="Owed to vendors" icon={Wallet} tone="red" test="payables-stat" />
        <Stat label="Expiring in 90 days" value={s.expiring_soon || 0} change="Watch these batches" icon={CalendarClock} tone="amber" test="expiring-stat" />
        <Stat label="Active vendors" value={s.vendors || 0} change="Supply partners" icon={Truck} tone="blue" test="vendors-stat" />
      </section>

      <div className="dashboard-grid">
        <section className="panel attention-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">ACTION QUEUE</p>
              <h3>Needs your attention</h3>
            </div>
            <button className="text-btn" data-testid="view-all-indents-button" onClick={() => onNavigate("indents")}>View all <ArrowUpRight size={15} /></button>
          </div>
          {(data.indents || []).slice(0, 4).map((x, i) => (
            <div className="attention-row" key={x.id || i}>
              <div className={`row-icon ${i === 0 ? "amber" : "teal"}`}><ClipboardList size={17} /></div>
              <div className="row-copy">
                <strong>{x.id}</strong>
                <span>{x.department} · {x.items?.[0]?.qty || "—"} {x.items?.[0]?.unit || "items"}</span>
              </div>
              <StatusBadge value={x.status} />
            </div>
          ))}
        </section>

        <section className="panel stock-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">STOCK SNAPSHOT</p>
              <h3>Fast moving items</h3>
            </div>
          </div>
          {(data.items || []).slice(0, 5).map((x) => (
            <div className="stock-row" key={x.id}>
              <div className="medicine-avatar">{x.category === "Equipment" ? "EQ" : x.category === "Consumable" ? "Cn" : "Rx"}</div>
              <div className="row-copy">
                <strong>{x.name}</strong>
                <span>{x.batch} · {x.base_unit || x.unit}</span>
              </div>
              <div className="stock-number">
                <strong>{Number(x.stock || 0).toLocaleString()}</strong>
                <span>{x.stock <= x.reorder_level ? "below reorder" : "in stock"}</span>
              </div>
            </div>
          ))}
        </section>
      </div>

      <section className="quick-band">
        <div>
          <p className="eyebrow">STORE HEALTH</p>
          <h3>Every item accounted for.</h3>
          <p>Track batches, expiry, packaging conversions, and delivery sign-offs in one place.</p>
        </div>
        <div className="health-meter">
          <div><span>Stock accuracy</span><b>96.8%</b></div>
          <div className="meter"><i style={{ width: "96.8%" }} /></div>
          <small>Based on latest receiving and issue records</small>
        </div>
      </section>
    </>
  );
}
